import {
  Button,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { getShippingEstimateText } from 'utils/format';

import CarrierTable from './carrierTable';
import { usePackage } from './packageProvider';
import { useShipping } from './shippingProvider';

export default function SelectCarrier({ onPrev, onNext }) {
  const {
    order,
    handleUpload,
    shipMethod,
    setShipMethod,
  } = usePackage();
  const {
    shipOrderId,
    handlePurchaseLabel,
    selectedRates, setSelectedRates,
    shipments,
  } = useShipping();

  const [isLoading, setIsLoading] = useState(false);

  const handleSkipPurchase = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!shipMethod) return;
      await handleUpload(shipments);
      onNext();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error skipping purchase', error);
    } finally {
      setIsLoading(false);
    }
  }, [handleUpload, onNext, shipments, shipMethod]);

  const handleNext = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!shipOrderId || !shipMethod) return;
      await new Promise((resolve, reject) => {
        handlePurchaseLabel({
          orderId: order.id,
          onSuccess: async (data) => {
            try {
              await handleUpload(data.shipments);

              onNext();
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          onError: reject,
        });
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error purchasing label', error);
    } finally {
      setIsLoading(false);
    }
  }, [handlePurchaseLabel, handleUpload, onNext, order.id, shipOrderId, shipMethod]);

  const handlePrev = () => {
    setSelectedRates([]);
    onPrev();
  };

  if (!shipOrderId) return null;

  return (
    <SpaceBetween size="m">
      <CarrierTable onShipSelect={setShipMethod} />
      <Container
        header={(
          <Header description={getShippingEstimateText(order.shipCharged, order.packageCount)}>
            Shipping Method
          </Header>
        )}
      >
        <FormField
          constraintText="Need a different option? You can override this."
          errorText={!shipMethod && 'Please enter or select a Delivery Service'}
        >
          <Input
            value={shipMethod}
            onChange={({ detail }) => setShipMethod(detail.value)}
          />
        </FormField>
      </Container>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
        <Button onClick={handlePrev}>Back</Button>
        <Button loading={isLoading} onClick={handleSkipPurchase}>Skip Purchase</Button>
        <Button variant="primary" loading={isLoading} onClick={handleNext}>
          {`Buy Label${selectedRates.length > 0 ? ` for $${selectedRates[0].price}` : ''}`}
        </Button>
      </div>
    </SpaceBetween>
  );
}

SelectCarrier.propTypes = {
  onNext: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
};
